/**
 * API Utility - Centralized API configuration with versioning support
 * 
 * Usage:
 *   import { api, API_URL } from '@/utils/api';
 *   
 *   // Using axios instance (recommended)
 *   const response = await api.get('/trips');
 *   
 *   // Using URL directly
 *   fetch(`${API_URL}/trips`)
 */
import axios from 'axios';
import { getCurrentUser } from './auth';
import {
  getAccessToken,
  refreshAccessToken,
  clearStoredTokens,
} from './tokenRefresh';

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_VERSION = 'v1';

// API Base URL with version prefix
export const API_URL = `${BACKEND_URL}/api/${API_VERSION}`;

// Legacy API URL (for backward compatibility during migration)
export const API_URL_LEGACY = `${BACKEND_URL}/api`;

// Create axios instance with default configuration
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Resolve the Bearer token to put on outgoing requests. Phase 2 (Feb 2026)
// prefers the short-lived access_token; falls back to the legacy 30-day
// session_token while clients are mid-rollout so old browsers/tabs that
// were logged in before Phase 2 keep working until they refresh the page.
const _resolveBearer = () => getAccessToken() || localStorage.getItem('session_token');

// Request interceptor to add auth headers
api.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    // Respect an Authorization header the CALLER set explicitly (e.g. the
    // Google-return page hydrating /auth/me with a freshly-minted token).
    // Overriding it with whatever token is in localStorage would log the
    // user in as whoever was previously signed in on this browser — the
    // classic shared-device / account-switch identity bug. When an explicit
    // token is present we also skip X-User-ID so we never send a stale id
    // that disagrees with the token.
    const hasExplicitAuth = !!(config.headers['Authorization'] || config.headers['authorization']);
    if (hasExplicitAuth) {
      return config;
    }
    const user = getCurrentUser();
    if (user?.id) {
      config.headers['X-User-ID'] = user.id;
    }
    const bearer = _resolveBearer();
    if (bearer) {
      config.headers['Authorization'] = `Bearer ${bearer}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// Response interceptor: catch 401 → try ONE refresh → retry the request.
//
// Concurrency: the actual refresh lives in tokenRefresh.refreshAccessToken
// and de-dupes parallel callers so we don't stampede /auth/refresh. If the
// refresh itself fails we clear all stored tokens and surface the original
// 401 — the calling app is responsible for redirecting to /login.
//
// We mark retried requests with `config._retried = true` so a second 401
// after a fresh access token (e.g. forbidden, not auth) doesn't loop.
// We also bypass the whole flow when the URL is /auth/refresh — that 401
// should propagate untouched.
// ---------------------------------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const apiVersion = error.response?.headers?.['x-api-version'];
    if (apiVersion) console.debug(`API Version: ${apiVersion}`);

    const status = error.response?.status;
    const config = error.config;
    const url = config?.url || '';

    if (status === 401 && config && !config._retried && !url.includes('/auth/refresh')) {
      config._retried = true;
      try {
        const newAccess = await refreshAccessToken();
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${newAccess}`;
        return api.request(config);
      } catch (_refreshErr) {
        // Refresh failed — token storage is already cleared. Let the
        // original 401 bubble up so the caller can redirect to login.
      }
    }
    return Promise.reject(error);
  }
);

// Re-export getAuthHeaders for convenience (canonical source is auth.js)
export { getAuthHeaders } from './auth';

// ---------------------------------------------------------------------------
// Global idempotency interceptor (applies to BOTH `api` and raw `axios`).
//
// Stamps an `Idempotency-Key` header onto every mutating request (POST/PUT/
// PATCH). The key is a SHA-1 hash of { method, url, body, time-bucket }, so a
// retry of the same logical operation (slow network, user double-click, stale
// UI click, browser navigation retry) produces the SAME key within a short
// window — the backend then dedupes and returns the cached response instead
// of creating a duplicate row.
//
// Time-bucketing (120s) prevents legitimate "add two identical things on
// purpose a few minutes apart" from colliding — only rapid retries dedupe.
//
// Callers can override by setting their own `Idempotency-Key` header.
// ---------------------------------------------------------------------------
const _stableStringify = (v) => {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(_stableStringify).join(',') + ']';
  return '{' + Object.keys(v).sort()
    .map(k => JSON.stringify(k) + ':' + _stableStringify(v[k])).join(',') + '}';
};

const _hashSHA1 = async (s) => {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const buf = new TextEncoder().encode(s);
    const digest = await crypto.subtle.digest('SHA-1', buf);
    return Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0')).join('');
  }
  // Fallback (non-secure contexts) — deterministic djb2
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h) + s.charCodeAt(i);
  return (h >>> 0).toString(16);
};

const _BUCKET_SECONDS = 120;

const _attachIdempotencyKey = async (config) => {
  const method = (config.method || '').toLowerCase();
  if (!['post', 'put', 'patch'].includes(method)) return config;
  const headers = config.headers || {};
  if (headers['Idempotency-Key'] || headers['idempotency-key']) return config;
  try {
    const bucket = Math.floor(Date.now() / 1000 / _BUCKET_SECONDS);
    const payload = _stableStringify({
      m: method,
      u: config.url || '',
      b: config.data ?? null,
      t: bucket,
    });
    const key = await _hashSHA1(payload);
    config.headers = { ...headers, 'Idempotency-Key': key };
  } catch (_e) {
    // Hashing failure must NEVER block the request
  }
  return config;
};

// Attach to the `api` instance
api.interceptors.request.use(_attachIdempotencyKey, (e) => Promise.reject(e));
// Attach to the default axios too (most callers in this codebase use raw axios)
axios.interceptors.request.use(_attachIdempotencyKey, (e) => Promise.reject(e));

// ---------------------------------------------------------------------------
// Global auth interceptor (applies to raw `axios` so legacy call sites stop
// 401-ing). The `api` instance already attaches auth in its own interceptor
// up top — this one mirrors the same logic onto the default axios so that
// `axios.get(`${API}/foo`)` Just Works™ everywhere.
//
// Skipped for cross-origin URLs that are NOT this app's backend (e.g. third-
// party CDN calls) to avoid leaking the session token off-site.
// ---------------------------------------------------------------------------
const _attachAuthHeaders = (config) => {
  // Only attach when the request is going to our backend.
  const url = config.url || '';
  const isOurBackend =
    url.startsWith(BACKEND_URL) ||
    url.startsWith('/api') ||
    !/^https?:\/\//.test(url); // relative URL → our app
  if (!isOurBackend) return config;

  const headers = config.headers || {};
  // Don't overwrite explicitly set headers — caller intent wins.
  if (!headers['X-User-ID'] && !headers['x-user-id']) {
    const user = getCurrentUser();
    if (user?.id) headers['X-User-ID'] = user.id;
  }
  if (!headers['Authorization'] && !headers['authorization']) {
    const bearer = _resolveBearer();
    if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
  }
  config.headers = headers;
  return config;
};
axios.interceptors.request.use(_attachAuthHeaders, (e) => Promise.reject(e));

// Mirror the response-side auto-refresh onto the raw axios too, for the
// long tail of legacy call sites that haven't been migrated to `api`.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const config = error.config;
    const url = config?.url || '';
    const isOurBackend =
      url.startsWith(BACKEND_URL) ||
      url.startsWith('/api') ||
      !/^https?:\/\//.test(url);

    if (status === 401 && config && !config._retried && isOurBackend && !url.includes('/auth/refresh')) {
      config._retried = true;
      try {
        const newAccess = await refreshAccessToken();
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${newAccess}`;
        return axios.request(config);
      } catch (_refreshErr) {
        // Storage already cleared by refreshAccessToken on failure.
      }
    }
    return Promise.reject(error);
  }
);

// Expose for callers that want to clear tokens directly (logout flow).
export { clearStoredTokens };

export default api;
